import { LogisticsTicket, LogisticsTicketQuote, LogisticsTicketQuoteRequest } from "types/logisticsTicket";
import { formatCentsAmount } from "utils/currency";
import { PipelinePickup, PipelineStage } from "./types";
import { getRegionFromLocation } from "./pipelineUtils";

const EXCLUDED_TICKET_STATES = new Set(["canceled"]);

export function mapTicketStateToPipelineStage(state: LogisticsTicket["state"]): PipelineStage | null {
  switch (state) {
    case "new_orders":
    case "pending_confirmation":
    case "scheduled_confirmed":
    case "picked_up":
    case "delivery_verified":
      return state;
    default:
      return null;
  }
}

function getAssetCount(ticket: LogisticsTicket): number {
  return ticket.pickup.pickupEstimatedItems.reduce((sum, item) => sum + (item.quantity || 0), 0);
}

function getPalletCount(ticket: LogisticsTicket): number {
  const palletsCount = ticket.pickup.pickupFreight?.palletsCount;
  if (palletsCount == null || palletsCount === "") {
    return 0;
  }

  return Number(palletsCount) || 0;
}

function getDisplayId(ticket: LogisticsTicket): string {
  if (ticket.pickup.orderNumber) {
    return ticket.pickup.orderNumber;
  }

  return `PU-${ticket.pickup.id}`;
}

function getRequestedDate(ticket: LogisticsTicket): string {
  return ticket.pickup.requestedDate?.split("T")[0] ?? ticket.pickup.scheduledAt?.split("T")[0] ?? "";
}

function formatPriceLabel(amountInCents: number, vendorName?: string): string {
  if (amountInCents === 0 || vendorName?.toLowerCase().includes("revivn")) {
    return "$0 - Internal Fleet";
  }

  return formatCentsAmount(amountInCents);
}

function getLowestQuote(ticket: LogisticsTicket): LogisticsTicketQuote | undefined {
  const quotes = ticket.quoteRequests
    .map((request) => request.quote)
    .filter((quote): quote is LogisticsTicketQuote => !!quote);

  if (quotes.length === 0) {
    return ticket.winningQuote;
  }

  return quotes.reduce((lowest, quote) => (quote.amountInCents < lowest.amountInCents ? quote : lowest));
}

function getRecommendedQuoteRequest(ticket: LogisticsTicket): LogisticsTicketQuoteRequest | undefined {
  const lowestQuote = getLowestQuote(ticket);
  if (!lowestQuote) {
    return undefined;
  }

  return ticket.quoteRequests.find((request) => request.quote?.id === lowestQuote.id);
}

function getLatestSentQuoteRequest(ticket: LogisticsTicket): LogisticsTicketQuoteRequest | undefined {
  return [...ticket.quoteRequests]
    .filter((request) => request.sentAt)
    .sort((a, b) => new Date(b.sentAt ?? 0).getTime() - new Date(a.sentAt ?? 0).getTime())[0];
}

function getWinningQuoteRequest(ticket: LogisticsTicket): LogisticsTicketQuoteRequest | undefined {
  if (!ticket.winningQuote) {
    return undefined;
  }

  return ticket.quoteRequests.find((request) => request.quote?.id === ticket.winningQuote?.id);
}

function formatScheduledAt(ticket: LogisticsTicket, quote?: LogisticsTicketQuote): string {
  if (quote?.proposedDate) {
    const timeWindow = quote.proposedTimeWindow ?? ticket.pickup.confirmedTime;
    return timeWindow ? `${quote.proposedDate} ${timeWindow}` : quote.proposedDate;
  }

  const date = getRequestedDate(ticket);
  if (!date) {
    return "";
  }

  return ticket.pickup.confirmedTime ? `${date} ${ticket.pickup.confirmedTime}` : date;
}

function getLogisticsAgentName(ticket: LogisticsTicket): string | undefined {
  const pickupAgent = ticket.pickup.pickupAgent as { name?: string; agent?: { name?: string } } | undefined;
  return pickupAgent?.name || pickupAgent?.agent?.name;
}

function getCoordinatorName(ticket: LogisticsTicket): string | undefined {
  const coordinator = ticket.pickup.revivnCoordinator;
  if (!coordinator) {
    return undefined;
  }

  return [coordinator.firstName, coordinator.lastName].filter(Boolean).join(" ") || undefined;
}

export function mapLogisticsTicketToPipelinePickup(ticket: LogisticsTicket): PipelinePickup | null {
  if (EXCLUDED_TICKET_STATES.has(ticket.state)) {
    return null;
  }

  const stage = mapTicketStateToPipelineStage(ticket.state);
  if (!stage) {
    return null;
  }

  const requestedDate = getRequestedDate(ticket);
  const pipelinePickup: PipelinePickup = {
    id: String(ticket.id),
    uuid: ticket.uuid,
    displayId: getDisplayId(ticket),
    companyName: ticket.pickup.organization?.name ?? "Unknown company",
    region: getRegionFromLocation(ticket.pickup.location),
    city: ticket.pickup.location?.city ?? "",
    state: ticket.pickup.location?.state ?? "",
    location: ticket.pickup.location?.fullAddress ?? "",
    assetCount: getAssetCount(ticket),
    palletCount: getPalletCount(ticket),
    requestedDate,
    timeWindow: ticket.pickup.confirmedTime || ticket.pickup.requestedTime || ticket.winningQuote?.proposedTimeWindow,
    coordinatorName: getCoordinatorName(ticket),
    logisticsAgentName: getLogisticsAgentName(ticket),
    stage,
    needsAction: ticket.needsAction,
    cancellationReason: ticket.cancellationReason,
  };

  const recommendedRequest = getRecommendedQuoteRequest(ticket);
  const recommendedQuote = getLowestQuote(ticket);
  const latestSentRequest = getLatestSentQuoteRequest(ticket);
  const winningRequest = getWinningQuoteRequest(ticket);

  if (stage === "new_orders" && recommendedQuote && recommendedRequest?.vendor) {
    pipelinePickup.autoRecommendation = {
      vendorName: recommendedRequest.vendor.name,
      priceLabel: formatPriceLabel(recommendedQuote.amountInCents, recommendedRequest.vendor.name),
      instantBook: recommendedRequest.vendor.name.toLowerCase().includes("revivn"),
    };
  }

  if (stage === "pending_confirmation") {
    const pendingRequest = winningRequest ?? latestSentRequest;
    if (pendingRequest?.vendor) {
      pipelinePickup.pendingVendor = {
        vendorName: pendingRequest.vendor.name,
        sentAt: pendingRequest.sentAt ?? pendingRequest.customerSentAt ?? ticket.updatedAt,
      };
    }
  }

  if (stage === "scheduled_confirmed") {
    const confirmedRequest = winningRequest ?? latestSentRequest;
    const confirmedQuote = ticket.winningQuote ?? confirmedRequest?.quote;
    if (confirmedRequest?.vendor) {
      pipelinePickup.confirmedVendor = {
        vendorName: confirmedRequest.vendor.name,
        scheduledAt: formatScheduledAt(ticket, confirmedQuote),
      };
    }
  }

  if (stage === "picked_up" && ticket.startedAt) {
    pipelinePickup.pickedUpAt = ticket.startedAt;
  }

  if (stage === "delivery_verified" && ticket.completedAt) {
    pipelinePickup.deliveredAt = ticket.completedAt;
  }

  return pipelinePickup;
}

export function mapLogisticsTicketsToPipelinePickups(tickets: LogisticsTicket[]): PipelinePickup[] {
  return tickets.map(mapLogisticsTicketToPipelinePickup).filter((pickup): pickup is PipelinePickup => pickup !== null);
}
